import { Box, Chip } from '@mui/joy';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { IContact, IContactForm, IContactGroup } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { TextEllipsis } from 'core/elements';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { corporateService } from 'corporate/services';
import { Variant } from 'theme/enums';

import { DefaultLocationsFormFields } from './DefaultLocationsFormFields';

export type THiddenFields = {
  isDefault?: boolean;
};

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IContactForm>) => void;
  hiddenFields?: THiddenFields;
  contact?: IContact;
  contactGroup?: Partial<IContactGroup>;
  onSubmit: (reset: UseFormReset<IContactForm>) => (data: IContactForm) => void;
  level?: number;
}

export const UpsertContactFormFields = ({
  isOpen,
  onClose,
  hiddenFields,
  contact,
  contactGroup,
  onSubmit,
  level = 0,
}: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });
  const isEdit = useMemo(() => !!contact?.id, [contact]);

  const emailHelperTexts = useMemo(() => {
    if (!contactGroup?.domains?.length) {
      return [];
    }

    return [
      t('labelAvailableEmailDomains'),
      <TextEllipsis
        key="domains"
        content={
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              padding: '4px 0',
              gap: '8px',
            }}
          >
            {contactGroup.domains?.map((domain: string) => (
              <Chip key={domain} variant={Variant.OUTLINED}>{`@${domain}`}</Chip>
            ))}
          </Box>
        }
        lineHeight={32}
        maxRows={2}
        showMoreLabel
        showMoreSxProps={{
          bottom: 0,
          left: '4px',
        }}
      />,
    ];
  }, [contactGroup.domains, t]);

  return (
    <Form
      isOpen={isOpen}
      onClose={onClose}
      title={contact?.id ? t('titleEditContact') : t('titleAddContact')}
      subtitle={contactGroup?.name}
      onSubmit={onSubmit}
      level={level}
    >
      <FormField
        name="firstName"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelFirstName'),
          required: true,
        }}
      />

      <FormField
        name="lastName"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelLastName'),
          required: true,
        }}
      />

      <FormField
        name="email"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelEmail'),
          helperTexts: emailHelperTexts,
          required: true,
        }}
      />

      <FormField
        name="phone"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelOfficePhoneNumber'),
        }}
      />

      <FormField
        name="mobile"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelMobilePhoneNumber'),
        }}
      />

      {!isAlternateVersionEnabled && (
        <FormField
          name="contactGrouping"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelContactGrouping'),
            getOptions: corporateService.getContactGroupings,
          }}
        />
      )}

      <DefaultLocationsFormFields contactGroup={contactGroup} level={level} />

      {!hiddenFields?.isDefault && (
        <FormField
          name="isDefault"
          type={InputType.SWITCH}
          inputProps={{ label: t('labelDefault') }}
        />
      )}

      {!isEdit && (
        <FormField
          name="sendInvitation"
          type={InputType.CHECKBOX}
          inputProps={{ label: t('labelSendInvitation') }}
        />
      )}
    </Form>
  );
};
